import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useStaffMember } from './useStaffMember';
import { hasPermission, canViewPage, canEdit, canDelete, getRolePermissions } from '../../utils/rolePermissions';
import { getEffectivePermissions, analyzeRolePermissionAlignment } from '../../utils/rolePermissionSync';
import { useDevRoleOverride } from '../../components/dev/RoleToggle';

// Developer override for testing - set to false for production
const DEV_OVERRIDE = process.env.NODE_ENV === 'development';

/**
 * Hook to check role-based permissions
 * @returns {object} Permission checking functions
 */
export const useRolePermissions = () => {
  const { user } = useAuth();
  const { staffMember, loading } = useStaffMember(user?.uid);
  const devRoleOverride = useDevRoleOverride();

  // Use dev role override in development, otherwise use actual role
  const userRole = DEV_OVERRIDE && devRoleOverride ? devRoleOverride : staffMember?.role;
  const userPermissions = staffMember?.permissions;
  
  // Get effective permissions using the synchronization utility
  const effectivePermissions = useMemo(() => {
    // In development mode with role override, create a mock staff member with the overridden role
    if (DEV_OVERRIDE && devRoleOverride) {
      const mockStaffMember = {
        ...staffMember,
        role: devRoleOverride,
        permissions: null // Use simple role system only
      };
      return getEffectivePermissions(mockStaffMember);
    }
    return getEffectivePermissions(staffMember);
  }, [staffMember, devRoleOverride]);

  const permissions = useMemo(() => ({
    /**
     * Check if user has permission for a specific action on a page
     * @param {string} page - Page name
     * @param {string} action - Action (view, edit, delete)
     * @returns {boolean}
     */
    hasPermission: (page, action = 'view') => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      if (action === 'view') return effectivePermissions.canView[page] || false;
      if (action === 'edit') return effectivePermissions.canEdit[page] || false;
      if (action === 'delete') return effectivePermissions.canDelete[page] || false;
      return false;
    },

    /**
     * Check if user can view a page
     * @param {string} page - Page name
     * @returns {boolean}
     */
    canViewPage: (page) => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      return effectivePermissions.canView[page] || false;
    },

    /**
     * Check if user can edit on a page
     * @param {string} page - Page name
     * @returns {boolean}
     */
    canEdit: (page) => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      return effectivePermissions.canEdit[page] || false;
    },

    /**
     * Check if user can delete on a page
     * @param {string} page - Page name
     * @returns {boolean}
     */
    canDelete: (page) => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      return effectivePermissions.canDelete[page] || false;
    },

    /**
     * Check if user is admin (for backwards compatibility)
     * @returns {boolean}
     */
    isAdmin: () => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      return effectivePermissions.isSystemAdmin === true || userRole === 'admin';
    },

    /**
     * Get user's role
     * @returns {string}
     */
    getRole: () => {
      return userRole;
    },

    /**
     * Get all permissions for the user's role
     * @returns {object}
     */
    getAllPermissions: () => {
      return getRolePermissions(userRole);
    },

    /**
     * Check if user has a specific permission group
     * @param {string} groupName - Permission group name
     * @returns {boolean}
     */
    hasPermissionGroup: (groupName) => {
      return userPermissions?.groups?.includes(groupName) || false;
    },

    /**
     * Check if user is system admin
     * @returns {boolean}
     */
    isSystemAdmin: () => {
      // While loading, default to false (most restrictive)
      if (loading && !DEV_OVERRIDE) return false;
      return userPermissions?.groups?.includes('system-admin') || userRole === 'admin';
    },

    /**
     * Get user's permission groups
     * @returns {array}
     */
    getPermissionGroups: () => {
      return userPermissions?.groups || [];
    },

    /**
     * Get user's staff data
     * @returns {object}
     */
    getStaffMember: () => {
      return staffMember;
    },

    /**
     * Get effective permissions summary
     * @returns {object}
     */
    getEffectivePermissions: () => {
      return effectivePermissions;
    },

    /**
     * Analyze role/permission alignment
     * @returns {object}
     */
    analyzePermissionAlignment: () => {
      return analyzeRolePermissionAlignment(staffMember);
    },

    /**
     * Check if user has high-level access
     * @returns {boolean}
     */
    hasHighLevelAccess: () => {
      return effectivePermissions.hasHighLevelAccess;
    },

    /**
     * Check if user has advanced permission system
     * @returns {boolean}
     */
    hasAdvancedPermissions: () => {
      return effectivePermissions.hasAdvancedPermissions;
    },

    /**
     * Check if permissions are still loading
     * @returns {boolean}
     */
    isLoading: () => {
      return loading;
    }
  }), [userRole, userPermissions, staffMember, effectivePermissions, loading]);

  return permissions;
};

export default useRolePermissions;